/**
 * ComparisonRow component for displaying a single metric row in the comparison table
 * Shows the same metric across multiple creatives with expandable details
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import * as React from 'react';

import type { ComparisonData } from '@/hooks/useComparison';
import { decisionColors, confidenceColors } from '@/lib/design-tokens';
import { cn } from '@/lib/utils';
import type { AnalysisDecision, Confidence } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export type MetricType =
  | 'decision'
  | 'confidence'
  | 'primaryReason'
  // Video brand metrics
  | 'logoDuration'
  | 'productDuration'
  | 'brandCueBy2s'
  // Image brand metrics
  | 'logoProminence'
  | 'productProminence'
  | 'brandIdentifiable'
  // Quality & Edits
  | 'qualityGate'
  | 'editsCount'
  // Performance metrics - Cost
  | 'spend'
  | 'impressions'
  // Performance metrics - View Rates
  | 'viewRate2s'
  | 'viewRate3s'
  | 'thruPlayRate'
  // Performance metrics - Engagement
  | 'ctr'
  | 'cpc'
  | 'cpm';

export interface ComparisonRowProps {
  label: string;
  metric: MetricType;
  items: ComparisonData[];
  isExpandable?: boolean;
  renderExpanded?: (item: ComparisonData) => React.ReactNode;
}

// ============================================================================
// VALUE RENDERERS
// ============================================================================

function renderDecision(decision: AnalysisDecision | undefined): React.ReactNode {
  if (!decision) {
    return <span className="text-muted text-sm">Not analyzed</span>;
  }

  const color = decisionColors[decision];
  return (
    <span
      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide"
      style={{ backgroundColor: color.bg, color: color.text }}
    >
      {decision}
    </span>
  );
}

function renderConfidence(confidence: Confidence | undefined): React.ReactNode {
  if (!confidence) {
    return <span className="text-muted text-sm">-</span>;
  }

  const color = confidenceColors[confidence];
  return (
    <span className="text-sm font-semibold" style={{ color }}>
      {confidence}
    </span>
  );
}

function renderDuration(seconds: number | undefined): React.ReactNode {
  if (seconds === undefined) {
    return <span className="text-muted text-sm">-</span>;
  }

  return (
    <span className="text-sm font-medium text-primary">{seconds.toFixed(1)}s</span>
  );
}

function renderBooleanCheck(value: boolean | undefined): React.ReactNode {
  if (value === undefined) {
    return <span className="text-muted text-sm">-</span>;
  }

  return value ? (
    <span className="inline-flex items-center gap-1.5 text-success text-sm font-medium">
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
      Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-danger text-sm font-medium">
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
      No
    </span>
  );
}

function renderCurrency(value: number | undefined): React.ReactNode {
  if (value === undefined) {
    return <span className="text-muted text-sm">-</span>;
  }

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return <span className="text-sm font-medium text-primary">{formatted}</span>;
}

function renderNumber(value: number | undefined): React.ReactNode {
  if (value === undefined) {
    return <span className="text-muted text-sm">-</span>;
  }

  const formatted = new Intl.NumberFormat('en-US').format(value);
  return <span className="text-sm font-medium text-primary">{formatted}</span>;
}

function renderPercentage(value: number | undefined, decimals: number = 1): React.ReactNode {
  if (value === undefined) {
    return <span className="text-muted text-sm">-</span>;
  }

  return (
    <span className="text-sm font-medium text-primary">{value.toFixed(decimals)}%</span>
  );
}

function renderProminence(prominence: string | undefined): React.ReactNode {
  if (!prominence) {
    return <span className="text-muted text-sm">-</span>;
  }

  const prominenceColors: Record<string, string> = {
    dominant: 'bg-success-soft text-success',
    visible: 'bg-info-soft text-info',
    subtle: 'bg-warning-soft text-warning',
    absent: 'bg-danger-soft text-danger',
  };

  return (
    <span
      className={cn(
        'inline-flex px-2 py-1 rounded text-xs font-semibold capitalize',
        prominenceColors[prominence] || 'bg-surface-subtle text-secondary'
      )}
    >
      {prominence}
    </span>
  );
}

function renderQualityGate(item: ComparisonData): React.ReactNode {
  const qualityGate = item.analysis?.result?.qualityGate;
  if (!qualityGate) {
    return <span className="text-muted text-sm">-</span>;
  }

  const checks = Object.values(qualityGate);
  const passCount = checks.filter((c) => c?.status === 'PASS').length;
  const totalCount = checks.length;

  const isPerfect = passCount === totalCount;

  return (
    <span
      className={cn(
        'text-sm font-semibold',
        isPerfect ? 'text-success' : 'text-warning'
      )}
    >
      {passCount}/{totalCount}
    </span>
  );
}

function renderEditsCount(item: ComparisonData): React.ReactNode {
  const edits = item.analysis?.result?.edits;
  if (!edits) {
    return <span className="text-muted text-sm">-</span>;
  }

  return (
    <span className="text-sm font-medium text-primary">
      {edits.length} edit{edits.length !== 1 ? 's' : ''}
    </span>
  );
}

function getValue(item: ComparisonData, metric: MetricType): React.ReactNode {
  const result = item.analysis?.result;
  const perfMetrics = item.creative.performanceMetrics;

  switch (metric) {
    case 'decision':
      return renderDecision(result?.decision.call);
    case 'confidence':
      return renderConfidence(result?.decision.confidence);
    case 'primaryReason':
      return result?.decision.primaryReason ? (
        <span className="text-sm text-secondary line-clamp-2">
          {result.decision.primaryReason}
        </span>
      ) : (
        <span className="text-muted text-sm">-</span>
      );
    case 'logoDuration':
      return renderDuration(
        result?.brandMetrics && 'logoDurationSeconds' in result.brandMetrics
          ? result.brandMetrics.logoDurationSeconds
          : undefined
      );
    case 'productDuration':
      return renderDuration(
        result?.brandMetrics && 'productDurationSeconds' in result.brandMetrics
          ? result.brandMetrics.productDurationSeconds
          : undefined
      );
    case 'brandCueBy2s':
      return renderBooleanCheck(
        result?.brandMetrics && 'brandCueBy2s' in result.brandMetrics
          ? result.brandMetrics.brandCueBy2s
          : undefined
      );
    // Image brand metrics
    case 'logoProminence':
      return renderProminence(
        result?.brandMetrics && 'logoProminence' in result.brandMetrics
          ? result.brandMetrics.logoProminence
          : undefined
      );
    case 'productProminence':
      return renderProminence(
        result?.brandMetrics && 'productProminence' in result.brandMetrics
          ? result.brandMetrics.productProminence
          : undefined
      );
    case 'brandIdentifiable':
      return renderBooleanCheck(
        result?.brandMetrics && 'brandIdentifiableAtGlance' in result.brandMetrics
          ? result.brandMetrics.brandIdentifiableAtGlance
          : undefined
      );
    case 'qualityGate':
      return renderQualityGate(item);
    case 'editsCount':
      return renderEditsCount(item);
    // Performance metrics - Cost
    case 'spend':
      return renderCurrency(perfMetrics?.spend);
    case 'impressions':
      return renderNumber(perfMetrics?.impressions);
    // Performance metrics - View Rates
    case 'viewRate2s':
      return renderPercentage(perfMetrics?.viewRate2s);
    case 'viewRate3s':
      return renderPercentage(perfMetrics?.viewRate3s);
    case 'thruPlayRate':
      return renderPercentage(perfMetrics?.thruPlayRate);
    // Performance metrics - Engagement
    case 'ctr':
      return renderPercentage(perfMetrics?.ctr, 2);
    case 'cpc':
      return renderCurrency(perfMetrics?.cpc);
    case 'cpm':
      return renderCurrency(perfMetrics?.cpm);
    default:
      return <span className="text-muted text-sm">-</span>;
  }
}

// ============================================================================
// COMPARISON ROW COMPONENT
// ============================================================================

export function ComparisonRow({
  label,
  metric,
  items,
  isExpandable = false,
  renderExpanded,
}: ComparisonRowProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <>
      {/* Main row */}
      <tr
        className={cn(
          'border-b border-border-subtle',
          isExpandable && 'cursor-pointer hover:bg-surface-subtle'
        )}
        onClick={isExpandable ? () => setIsExpanded(!isExpanded) : undefined}
      >
        {/* Label cell */}
        <td className="py-4 px-4 text-sm font-medium text-secondary sticky left-0 bg-surface">
          <div className="flex items-center gap-2">
            {isExpandable && (
              <span className="text-muted">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </span>
            )}
            {label}
          </div>
        </td>

        {/* Value cells */}
        {items.map((item) => (
          <td
            key={item.creative.creativeId}
            className="py-4 px-4 text-center"
          >
            {getValue(item, metric)}
          </td>
        ))}
      </tr>

      {/* Expanded content */}
      {isExpandable && isExpanded && renderExpanded && (
        <tr className="bg-surface-subtle">
          <td className="py-4 px-4 sticky left-0 bg-surface-subtle" />
          {items.map((item) => (
            <td
              key={`${item.creative.creativeId}-expanded`}
              className="py-4 px-4"
            >
              {renderExpanded(item)}
            </td>
          ))}
        </tr>
      )}
    </>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  renderDecision,
  renderConfidence,
  renderDuration,
  renderBooleanCheck,
  renderCurrency,
  renderNumber,
  renderPercentage,
};
